import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Search, 
  Filter, 
  Download, 
  Plus, 
  Trash2, 
  Edit2, 
  MoreVertical, 
  TrendingUp, 
  Users, 
  RefreshCw, 
  Instagram, 
  AlertCircle, 
  CheckCircle2, 
  Loader2, 
  ArrowUpRight, 
  Activity, 
  Target, 
  BarChart3,
  ChevronDown,
  Upload,
  ChevronLeft,
  ChevronRight,
  Youtube,
  Phone,
  Mail,
  MapPin,
  X,
  Check,
  Settings,
  Clock,
  TrendingDown,
  Megaphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import TrackerModal from './tracker/TrackerModal';
import YouTubeTrackerModal from './tracker/YouTubeTrackerModal';
import { api } from '@/utils/api';
import { formatNumber, formatCurrency } from '@/utils/formatters';
import { fetchInfluencer, getSavedTrackers, saveTracker, getProxiedImage } from '@/utils/instagramTrackerApi';
import ImportModal from './tracker/ImportModal';
import AdImportModal from './tracker/AdImportModal';

const calculateTier = (followers) => {
  if (followers < 10000) return 'nano';
  if (followers < 100000) return 'micro';
  if (followers < 500000) return 'mid';
  if (followers < 1000000) return 'macro';
  return 'mega';
};

const TIERS = ['nano', 'micro', 'mid', 'macro', 'mega'];
const CATEGORIES = ['lifestyle', 'fashion', 'beauty', 'tech', 'food', 'travel', 'fitness', 'entertainment', 'education', 'other'];
const STATUSES = ['active', 'inactive', 'on hold', 'blacklisted'];
const SOURCES = ['in-house', 'agency', 'pr'];
const BRANDS = ['brand a', 'brand b', 'both'];

const CATEGORY_EMOJI = {
  lifestyle: '✨', fashion: '👗', beauty: '💄', tech: '💻',
  food: '🍜', travel: '✈️', fitness: '💪', entertainment: '🎬',
  education: '📚', other: '🌟',
};

const getErBadge = (er) => {
  if (!er || er <= 0) return null;
  if (er >= 20) return { emoji: '🔥', classes: 'bg-orange-500/15 text-orange-500 border border-orange-500/20' };
  if (er >= 3)  return { emoji: '✅', classes: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' };
  return         { emoji: '⚠️', classes: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20' };
};

const getRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  const ms = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(ms / 60000);
  const hrs  = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (mins < 60) return { text: `${mins}m ago`, stale: false };
  if (hrs  < 24) return { text: `${hrs}h ago`,  stale: false };
  return { text: `${days}d ago`, stale: days >= 7 };
};

// Mini bar sparkline using CSS divs — reliable inside table cells
const Sparkline = ({ history = [], positive }) => {
  const vals = (history || []).filter(h => h.followers > 0).map(h => h.followers).slice(-7);
  if (vals.length < 2) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const H = 28;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: `${H}px`, flexShrink: 0 }}>
      {vals.map((v, i) => {
        const pct = Math.max(10, Math.round(((v - min) / range) * 100));
        const isLast = i === vals.length - 1;
        return (
          <div
            key={i}
            style={{
              width: '5px',
              height: `${pct}%`,
              borderRadius: '2px',
              backgroundColor: positive ? '#10b981' : '#ef4444',
              opacity: isLast ? 1 : 0.4,
              flexShrink: 0,
            }}
          />
        );
      })}
    </div>
  );
};

const InfluencerDatabase = ({ userRole }) => {
  const [loading, setLoading] = useState(true);
  const [allInfluencers, setAllInfluencers] = useState([]); // full dataset, loaded once
  const [allTrackers, setAllTrackers] = useState({});
  const [campaignPostsByHandle, setCampaignPostsByHandle] = useState({});
  const [stats, setStats] = useState(null);

  // Client-side filters
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterPoc, setFilterPoc] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Client-side pagination
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddToCampaignModal, setShowAddToCampaignModal] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);

  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  const [openRowMenu, setOpenRowMenu] = useState(null); // influencer.id of open row menu
  const rowMenuRef = useRef(null);
  
  // Tracker States
  const [trackingHandle, setTrackingHandle] = useState(null);
  const [ytTrackerInfluencer, setYtTrackerInfluencer] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAdImportModal, setShowAdImportModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState(new Set());


  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const SortIndicator = ({ column }) => {
    if (sortConfig.key !== column) return <ChevronDown className="w-3 h-3 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />;
    return <TrendingUp className={`w-3 h-3 text-salty-purple ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} />;
  };

  const fetchInfluencers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getInfluencers({ skip: 0, limit: 1000 });
      setAllInfluencers(response.data.influencers || []);
    } catch (error) {
      console.error('Error fetching influencers:', error);
      toast.error('Failed to load influencers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.getInfluencerStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const loadAllTrackerData = useCallback(async () => {
    try {
      const data = await getSavedTrackers();
      setAllTrackers(prev => ({ ...prev, ...(data || {}) }));
    } catch (err) {
      console.error('Failed to load trackers:', err);
    }
  }, []);

  const loadCampaignPosts = useCallback(async () => {
    try {
      const res = await api.getCampaignPostsByHandle();
      setCampaignPostsByHandle(res.data || {});
    } catch (err) {
      console.error('Failed to load campaign posts:', err);
    }
  }, []);

  useEffect(() => {
    fetchInfluencers();
    fetchStats();
    loadAllTrackerData();
    loadCampaignPosts();
  }, []);

  // Close the More menu when clicking outside
  useEffect(() => {
    if (!showMoreMenu) return;
    const handler = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMoreMenu]);

  // Close row action menu when clicking outside
  useEffect(() => {
    if (!openRowMenu) return;
    const handler = (e) => {
      if (rowMenuRef.current && !rowMenuRef.current.contains(e.target)) {
        setOpenRowMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openRowMenu]);

  const filteredInfluencers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allInfluencers.filter(inf => {
      if (q && ![inf.name, inf.email, inf.instagram_handle].some(v => v?.toLowerCase().includes(q))) return false;
      if (filterTier && inf.tier !== filterTier) return false;
      if (filterCategory && inf.category !== filterCategory) return false;
      if (filterStatus && inf.status !== filterStatus) return false;
      if (filterSource && inf.source !== filterSource) return false;
      if (filterBrand && inf.works_for !== filterBrand) return false;
      if (filterPoc && !inf.poc?.toLowerCase().includes(filterPoc.toLowerCase())) return false;
      if (filterPlatform) {
        const p = inf.platform || 'instagram';
        if (filterPlatform === 'youtube' && p !== 'youtube') return false;
        if (filterPlatform === 'instagram' && p !== 'instagram') return false;
        if (filterPlatform === 'both' && p !== 'both') return false;
      }
      return true;
    });
  }, [allInfluencers, search, filterTier, filterCategory, filterStatus, filterSource, filterBrand, filterPlatform, filterPoc]);

  const getSortedInfluencers = useMemo(() => () => {
    const enriched = filteredInfluencers.map(inf => {
      const handle = inf.instagram_handle?.replace('@', '').toLowerCase();
      const tracker = allTrackers[handle];
      const trackedAds = tracker?.trackedAds || [];
      const accountEr = parseFloat(tracker?.computed?.engagementRate) || inf.engagement_rate || 1;
      const avgVis = parseFloat(tracker?.computed?.avgReelViews) || 1;

      let performanceScore = 0;
      if (trackedAds.length > 0) {
        performanceScore = trackedAds.reduce((acc, ad) => {
          if (ad.format === 'Reel' && ad.reelViews > 0 && avgVis > 1) return acc + (ad.reelViews / avgVis);
          const adEr = (ad.likes + ad.comments) / (tracker?.profile?.followers || inf.followers_count || 1) * 100;
          return acc + (adEr / accountEr);
        }, 0) / trackedAds.length;
      }

      const history = (tracker?.history || []).filter(h => h.followers > 0);
      let growthScore = 0;
      if (history.length >= 2) {
        growthScore = history[history.length - 1].followers - history[history.length - 2].followers;
      }

      const perReel = inf.commercials?.rate_per_reel || 0;
      const reelAdsWithViews = trackedAds.filter(a => (a.reelViews || 0) > 0);
      const totalReelViews = reelAdsWithViews.reduce((s, a) => s + (a.reelViews || 0), 0);
      const cpvScore = perReel > 0 && totalReelViews > 0
        ? (perReel * reelAdsWithViews.length) / totalReelViews
        : null;

      return { ...inf, performanceScore, growthScore, cpvScore };
    });

    return [...enriched].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Always push nulls/undefined to the bottom regardless of sort direction
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredInfluencers, allTrackers, sortConfig]);

  useEffect(() => { setCurrentPage(0); }, [filteredInfluencers]);

  const handleSearch = (e) => { e.preventDefault(); }; // search is now live/instant

  const handleSeedData = async () => {
    try {
      setImporting(true);
      const response = await api.seedInfluencers();
      toast.success(`Imported ${response.data.imported} influencers, skipped ${response.data.skipped} duplicates`);
      fetchInfluencers();
      fetchStats();
    } catch (error) {
      console.error('Error seeding data:', error);
      toast.error('Failed to import influencers');
    } finally {
      setImporting(false);
    }
  };

  // Helper: sync one IG influencer and return updated fields + tracker data
  const syncInstagram = async (inf) => {
    const handle = inf.instagram_handle.replace(/^@+/, '').trim().toLowerCase();
    const data = await fetchInfluencer(handle);
    const followers = data.profile.followers || 0;
    const er = parseFloat(data.computed.engagementRate) || 0;
    const category = data.computed.primaryCategory?.toLowerCase() || inf.category;
    const now = new Date().toISOString();

    // Build history snapshot and persist to tracker
    const prevTracker = allTrackers[handle] || {};
    const prevHistory = prevTracker.history || [];
    const newSnapshot = {
      date: new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' }),
      timestamp: now,
      followers,
    };
    const updatedTrackerData = { ...data, history: [...prevHistory, newSnapshot] };
    await saveTracker(handle, updatedTrackerData);

    const updatedFields = {
      followers_count: followers,
      engagement_rate: er,
      tier: calculateTier(followers),
      category: CATEGORIES.includes(category) ? category : inf.category,
      status: 'active',
      instagram_profile_pic: data.profile.profilePicUrl || '',
      metrics_last_synced: now,
    };
    await api.updateInfluencer(inf.id, updatedFields);

    return { handle, trackerData: updatedTrackerData, updatedFields };
  };

  const handleSyncCommercialRates = async () => {
    try {
      const res = await api.syncCommercialRates();
      const { updated_influencer_profiles, updated_campaign_links, total_links_scanned, links_with_fee } = res.data;
      toast.success(`Rates synced — ${updated_influencer_profiles} profiles updated, ${updated_campaign_links} campaign links updated (scanned ${total_links_scanned} links, ${links_with_fee} had fees)`);
      await fetchInfluencers();
    } catch (err) {
      console.error('Rate sync failed:', err);
      toast.error('Failed to sync commercial rates');
    }
  };

  const handleSyncAllMetrics = async () => {
    try {
      setSyncing(true);
      const toSync = filteredInfluencers.filter(inf => {
        const platform = inf.platform || 'instagram';
        return (
          ((platform === 'instagram' || platform === 'both') && inf.instagram_handle) ||
          ((platform === 'youtube' || platform === 'both') && inf.youtube_handle)
        );
      });

      if (toSync.length === 0) {
        toast.info('No influencers with handles to sync on this view');
        setSyncing(false);
        return;
      }

      const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
      const needsSync = (inf) => {
        const last = inf.metrics_last_synced || inf.youtube_synced_at;
        if (!last) return true;
        return Date.now() - new Date(last).getTime() > FOUR_HOURS_MS;
      };

      const eligible = toSync.filter(needsSync);
      const skipped = toSync.length - eligible.length;

      if (eligible.length === 0) {
        toast.info('All influencers were synced within the last 4 hours — nothing to do');
        setSyncing(false);
        return;
      }

      toast.info(`Syncing ${eligible.length} influencer${eligible.length !== 1 ? 's' : ''}${skipped ? ` (${skipped} skipped — synced recently)` : ''}…`);

      let igSuccess = 0, ytSuccess = 0, failed = 0;
      const trackerUpdates = {};
      const influencerUpdates = {}; // id → merged fields

      for (let i = 0; i < eligible.length; i++) {
        const inf = eligible[i];
        const platform = inf.platform || 'instagram';

        if ((platform === 'instagram' || platform === 'both') && inf.instagram_handle) {
          try {
            const { handle, trackerData, updatedFields } = await syncInstagram(inf);
            trackerUpdates[handle] = trackerData;
            influencerUpdates[inf.id] = { ...inf, ...updatedFields };
            igSuccess++;
          } catch (err) {
            console.error(`IG sync failed for @${inf.instagram_handle}:`, err);
            failed++;
          }
          await new Promise(r => setTimeout(r, 500));
        }

        if ((platform === 'youtube' || platform === 'both') && inf.youtube_handle) {
          try {
            await api.syncYouTubeMetrics(inf.id);
            influencerUpdates[inf.id] = { ...(influencerUpdates[inf.id] || inf), youtube_synced_at: new Date().toISOString() };
            ytSuccess++;
          } catch (err) {
            console.error(`YT sync failed for ${inf.youtube_handle}:`, err);
            failed++;
          }
          await new Promise(r => setTimeout(r, 400));
        }
      }

      // Headless in-place state update — no page reload
      if (Object.keys(influencerUpdates).length > 0) {
        setAllInfluencers(prev => prev.map(inf => influencerUpdates[inf.id] ? { ...inf, ...influencerUpdates[inf.id] } : inf));
      }
      if (Object.keys(trackerUpdates).length > 0) {
        setAllTrackers(prev => ({ ...prev, ...trackerUpdates }));
      }

      toast.success(`Sync complete — IG: ${igSuccess}, YT: ${ytSuccess}${failed ? `, Failed: ${failed}` : ''}`);
    } catch (error) {
      console.error('Error in bulk sync:', error);
      toast.error('Failed to complete bulk sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncSingleMetrics = async (influencer) => {
    try {
      setSyncingId(influencer.id);
      const platform = influencer.platform || 'instagram';
      const igHandle = influencer.instagram_handle?.replace(/^@+/g, '').trim().toLowerCase();
      const ytHandle = influencer.youtube_handle?.replace(/^@+/g, '').trim().toLowerCase();

      if (platform === 'instagram' && !igHandle) {
        toast.warning('No Instagram handle found — edit the influencer to add one');
        return;
      }
      if (platform === 'youtube' && !ytHandle) {
        toast.warning('No YouTube handle found — edit the influencer to add one');
        return;
      }

      const lastSynced = influencer.metrics_last_synced || influencer.youtube_synced_at;
      if (lastSynced && Date.now() - new Date(lastSynced).getTime() < 4 * 60 * 60 * 1000) {
        const ago = Math.round((Date.now() - new Date(lastSynced).getTime()) / 60000);
        toast.info(`Already synced ${ago} min ago — skipping (next sync available after 4 hours)`);
        return;
      }

      let updatedFields = {};

      // Sync Instagram — saves tracker history snapshot so modal shows correct timestamp
      if ((platform === 'instagram' || platform === 'both') && igHandle) {
        const { trackerData, updatedFields: igFields } = await syncInstagram(influencer);
        setAllTrackers(prev => ({ ...prev, [igHandle]: trackerData }));
        updatedFields = { ...updatedFields, ...igFields };
        toast.success(`Synced @${igHandle} (Instagram)`);
      }

      // Sync YouTube
      if ((platform === 'youtube' || platform === 'both') && ytHandle) {
        const res = await api.syncYouTubeMetrics(influencer.id);
        const d = res.data;
        updatedFields = {
          ...updatedFields,
          youtube_subscribers: d.youtube_subscribers ?? d.subscribers,
          youtube_avg_views: d.youtube_avg_views ?? d.avg_views_per_video,
          youtube_total_views: d.youtube_total_views ?? d.total_views,
          youtube_video_count: d.youtube_video_count ?? d.video_count,
          youtube_synced_at: d.youtube_synced_at || d.synced_at,
        };
        toast.success(`Synced @${ytHandle} (YouTube)`);
      }

      // Headless in-place update — no page reload
      setAllInfluencers(prev => prev.map(inf =>
        inf.id === influencer.id ? { ...inf, ...updatedFields } : inf
      ));
    } catch (err) {
      console.error('Failed to sync:', err);
      toast.error('Failed to sync metrics');
    } finally {
      setSyncingId(null);
    }
  };

  // ── Multi-select helpers ──────────────────────────────────────
  // All sorted records (full filtered set) — used for multi-select "select all"
  const allSortedIds = useMemo(() => getSortedInfluencers().map(i => i.id), [getSortedInfluencers]);
  // Current page slice for rendering
  const pagedInfluencers = useMemo(() => {
    const sorted = getSortedInfluencers();
    if (pageSize === 0) return sorted; // 0 = show all
    return sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  }, [getSortedInfluencers, currentPage, pageSize]);
  const totalPages = pageSize === 0 ? 1 : Math.ceil(allSortedIds.length / pageSize);

  const allSelected = allSortedIds.length > 0 && allSortedIds.every(id => selectedIds.has(id));
  const someSelected = allSortedIds.some(id => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) { allSortedIds.forEach(id => next.delete(id)); }
      else { allSortedIds.forEach(id => next.add(id)); }
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkSync = async () => {
    const targets = getSortedInfluencers().filter(i => selectedIds.has(i.id) && i.instagram_handle);
    if (!targets.length) { toast.error('No selected influencers have Instagram handles'); return; }
    setSyncing(true);
    const trackerUpdates = {};
    let success = 0;
    for (let i = 0; i < targets.length; i++) {
      const inf = targets[i];
      const handle = inf.instagram_handle.replace(/^@+/, '').trim().toLowerCase();
      try {
        const { trackerData } = await syncInstagram(inf);
        trackerUpdates[handle] = trackerData;
        success++;
      } catch (err) { console.error(`Bulk sync failed @${handle}:`, err); }
      if (i < targets.length - 1) await new Promise(r => setTimeout(r, 500));
    }
    if (Object.keys(trackerUpdates).length) setAllTrackers(prev => ({ ...prev, ...trackerUpdates }));
    toast.success(`Synced ${success} of ${targets.length} influencers`);
    setSelectedIds(new Set());
    fetchInfluencers(); fetchStats();
    setSyncing(false);
  };

  const handleBulkStatusChange = async (newStatus) => {
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map(id => api.updateInfluencer(id, { status: newStatus })));
      toast.success(`Updated ${ids.length} influencer${ids.length > 1 ? 's' : ''} to "${newStatus}"`);
      setSelectedIds(new Set());
      fetchInfluencers();
    } catch (err) {
      console.error('Bulk status update failed:', err);
      toast.error('Failed to update status');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} influencer${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map(id => api.deleteInfluencer(id)));
      toast.success(`Deleted ${ids.length} influencer${ids.length > 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      fetchInfluencers(); fetchStats();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      toast.error('Failed to delete influencers');
    }
  };
  // ─────────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this influencer?')) return;
    
    try {
      await api.deleteInfluencer(id);
      toast.success('Influencer deleted');
      fetchInfluencers();
      fetchStats();
    } catch (error) {
      console.error('Error deleting influencer:', error);
      toast.error('Failed to delete influencer');
    }
  };

  return (
    <div data-testid="influencer-database-page">
      {/* Stats Cards */}
      {stats && (() => {
        const total     = stats.total_influencers || 0;
        const active    = stats.active_influencers || 0;
        const nanoMicro = (stats.by_tier?.nano || 0) + (stats.by_tier?.micro || 0);

        // Median ER — immune to outliers like 287%
        const erVals = allInfluencers.map(i => parseFloat(i.engagement_rate)).filter(v => v > 0 && v < 100).sort((a, b) => a - b);
        const medianEr = erVals.length
          ? erVals.length % 2 === 0
            ? (erVals[erVals.length / 2 - 1] + erVals[erVals.length / 2]) / 2
            : erVals[Math.floor(erVals.length / 2)]
          : 0;

        // Synced profiles
        const syncedCount = allInfluencers.filter(i => i.metrics_last_synced || i.instagram_profile_pic).length;
        const syncedPct   = total > 0 ? (syncedCount / total) * 100 : 0;

        // Tier distribution bar segments (Total card)
        const nano  = stats.by_tier?.nano  || 0;
        const micro = stats.by_tier?.micro || 0;
        const mid   = stats.by_tier?.mid   || 0;
        const macro = stats.by_tier?.macro || 0;
        const mega  = stats.by_tier?.mega  || 0;

        const KPIStatCard = ({ tier, label, value, sub, children, accent, clickHint, active: isActive }) => (
          <button
            onClick={() => tier ? setFilterTier(prev => prev === tier ? '' : tier) : null}
            className={`text-left w-full bg-salty-card border rounded-xl p-5 transition-all group ${
              tier ? 'cursor-pointer' : 'cursor-default'
            } ${isActive ? 'border-salty-purple ring-1 ring-salty-purple/30 shadow-sm' : 'border-salty-border hover:border-salty-purple/30'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-salty-text-muted font-outfit">{label}</p>
              {clickHint && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded transition-all ${isActive ? 'bg-salty-purple/10 text-salty-purple' : 'text-salty-text-muted opacity-0 group-hover:opacity-100'}`}>
                  {isActive ? '✕ clear' : 'filter →'}
                </span>
              )}
            </div>
            <p className={`font-outfit font-black text-[2rem] leading-none tabular-nums mb-1 ${accent || 'text-salty-text-main'}`}>
              {typeof value === 'number' ? formatNumber(value) : value}
            </p>
            <p className="text-[11px] text-salty-text-muted mb-3 leading-snug">{sub}</p>
            {children}
          </button>
        );

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="influencer-stats">

            {/* Card 1 — Total with tier breakdown bar */}
            <KPIStatCard label="TOTAL INFLUENCERS" value={total}
              sub={`${active} active · ${total - active} inactive`}
            >
              <div className="space-y-1.5">
                <div className="flex h-2 w-full rounded-full overflow-hidden gap-px">
                  {[
                    { count: nano,  color: 'bg-slate-400' },
                    { count: micro, color: 'bg-violet-400' },
                    { count: mid,   color: 'bg-teal-400' },
                    { count: macro, color: 'bg-amber-400' },
                    { count: mega,  color: 'bg-rose-400' },
                  ].filter(s => s.count > 0).map((seg, i) => (
                    <div key={i} className={`h-full ${seg.color} transition-all duration-700`} style={{ width: `${(seg.count / total) * 100}%` }} />
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { label: 'nano', count: nano, color: 'bg-slate-400' },
                    { label: 'micro', count: micro, color: 'bg-violet-400' },
                    { label: 'mid', count: mid, color: 'bg-teal-400' },
                    { label: 'macro', count: macro, color: 'bg-amber-400' },
                    { label: 'mega', count: mega, color: 'bg-rose-400' },
                  ].filter(s => s.count > 0).map(s => (
                    <span key={s.label} className="flex items-center gap-1 text-[9px] text-salty-text-muted font-bold uppercase">
                      <span className={`w-1.5 h-1.5 rounded-full ${s.color}`} />{s.label} {s.count}
                    </span>
                  ))}
                </div>
              </div>
            </KPIStatCard>

            {/* Card 2 — Nano/Micro clickable */}
            <KPIStatCard tier="micro" label="NANO / MICRO" value={nanoMicro}
              sub={`${((nanoMicro / (total || 1)) * 100).toFixed(0)}% of network · under 50K followers`}
              clickHint active={filterTier === 'micro'}
              accent={filterTier === 'micro' ? 'text-salty-purple' : undefined}
            >
              <div className="h-1.5 w-full bg-salty-subtle rounded-full overflow-hidden">
                <div className="h-full bg-salty-teal rounded-full transition-all duration-700"
                  style={{ width: `${(nanoMicro / (total || 1)) * 100}%` }} />
              </div>
            </KPIStatCard>

            {/* Card 3 — Median ER */}
            <KPIStatCard label="MEDIAN ENGAGEMENT"
              value={`${medianEr.toFixed(1)}%`}
              sub={medianEr >= 3
                ? `Above 3% benchmark · ${erVals.length} synced`
                : `Below 3% benchmark · ${erVals.length} synced`}
              accent={medianEr >= 3 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}
            >
              <div className="h-1.5 w-full bg-salty-subtle rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${medianEr >= 3 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                  style={{ width: `${Math.min(medianEr * 10, 100)}%` }} />
              </div>
            </KPIStatCard>

            {/* Card 4 — Synced profiles */}
            <KPIStatCard label="PROFILES SYNCED"
              value={syncedCount}
              sub={`${syncedPct.toFixed(0)}% have live data · ${total - syncedCount} pending`}
              accent={syncedPct === 100 ? 'text-emerald-600 dark:text-emerald-400' : undefined}
            >
              <div className="h-1.5 w-full bg-salty-subtle rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${syncedPct >= 80 ? 'bg-emerald-500' : syncedPct >= 50 ? 'bg-salty-amber' : 'bg-rose-500'}`}
                  style={{ width: `${syncedPct}%` }} />
              </div>
            </KPIStatCard>

          </div>
        );
      })()}

      {/* Search and Actions Bar */}
      <div className="bg-salty-card border border-salty-border rounded-xl p-4 mb-6">
        {/* Row 1: search + actions */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, email, or handle..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 bg-salty-bg border-salty-border text-salty-text-main"
              data-testid="influencer-search-input"
            />
          </div>

          {/* Filter toggle */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={`border-salty-border shrink-0 whitespace-nowrap transition-colors ${showFilters ? 'bg-salty-purple/10 border-salty-purple/40 text-salty-purple' : 'text-salty-text-muted hover:bg-salty-bg'}`}
            data-testid="filter-toggle-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {[filterTier, filterCategory, filterStatus, filterSource, filterBrand, filterPlatform, filterPoc].filter(Boolean).length > 0 && (
              <span className="ml-2 bg-salty-purple text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {[filterTier, filterCategory, filterStatus, filterSource, filterBrand, filterPlatform, filterPoc].filter(Boolean).length}
              </span>
            )}
          </Button>

          <div className="flex-1" />

          {/* Secondary actions — click-to-open dropdown */}
          {userRole !== 'viewer' && (
            <div className="relative" ref={moreMenuRef}>
              <Button
                variant="outline"
                className="border-salty-border text-salty-text-muted hover:bg-salty-bg shrink-0"
                onClick={() => setShowMoreMenu(v => !v)}
              >
                <MoreVertical className="w-4 h-4 mr-1" />
                More
              </Button>
              {showMoreMenu && (
                <div className="absolute right-0 top-full mt-1 flex flex-col bg-salty-card border border-salty-border rounded-xl shadow-xl overflow-hidden min-w-[160px] z-20">
                  <button
                    onClick={() => { setShowMoreMenu(false); handleSyncAllMetrics(); }}
                    disabled={syncing}
                    className="flex items-center gap-2 px-4 py-2.5 text-xs text-salty-text-main hover:bg-salty-bg transition-colors disabled:opacity-50 text-left"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 text-salty-teal ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing…' : 'Sync All Metrics'}
                  </button>
                  <button
                    onClick={() => { setShowMoreMenu(false); handleSyncCommercialRates(); }}
                    className="flex items-center gap-2 px-4 py-2.5 text-xs text-salty-text-main hover:bg-salty-bg transition-colors text-left"
                  >
                    <RefreshCw className="w-3.5 h-3.5 text-emerald-400" />
                    Sync Reel Rates
                  </button>
                  <button
                    onClick={() => { setShowMoreMenu(false); setShowAdImportModal(true); }}
                    className="flex items-center gap-2 px-4 py-2.5 text-xs text-salty-text-main hover:bg-salty-bg transition-colors text-left"
                  >
                    <Activity className="w-3.5 h-3.5 text-salty-purple" />
                    Import Ads
                  </button>
                  <button
                    onClick={() => { setShowMoreMenu(false); setShowImportModal(true); }}
                    disabled={importing}
                    className="flex items-center gap-2 px-4 py-2.5 text-xs text-salty-text-main hover:bg-salty-bg transition-colors disabled:opacity-50 text-left"
                    data-testid="import-excel-btn"
                  >
                    <Upload className="w-3.5 h-3.5 text-salty-amber" />
                    {importing ? 'Importing…' : 'Import Excel'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Primary CTA */}
          {userRole !== 'viewer' && (
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-salty-purple hover:bg-salty-purple-light shrink-0 whitespace-nowrap"
              data-testid="add-influencer-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Influencer
            </Button>
          )}
        </div>

        {/* Active filter chips */}
        {[
          { key: 'tier',     value: filterTier,     label: filterTier,     clear: () => setFilterTier('') },
          { key: 'category', value: filterCategory, label: filterCategory, clear: () => setFilterCategory('') },
          { key: 'status',   value: filterStatus,   label: filterStatus,   clear: () => setFilterStatus('') },
          { key: 'source',   value: filterSource,   label: filterSource,   clear: () => setFilterSource('') },
          { key: 'brand',    value: filterBrand,    label: filterBrand,    clear: () => setFilterBrand('') },
          { key: 'platform', value: filterPlatform, label: filterPlatform, clear: () => setFilterPlatform('') },
          { key: 'poc',      value: filterPoc,      label: `POC: ${filterPoc}`, clear: () => setFilterPoc('') },
        ].filter(f => f.value).length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-salty-border">
            <span className="text-[10px] font-bold uppercase tracking-widest text-salty-text-muted">Active filters:</span>
            {[
              { key: 'tier',     value: filterTier,     clear: () => setFilterTier('') },
              { key: 'category', value: filterCategory, clear: () => setFilterCategory('') },
              { key: 'status',   value: filterStatus,   clear: () => setFilterStatus('') },
              { key: 'source',   value: filterSource,   clear: () => setFilterSource('') },
              { key: 'brand',    value: filterBrand,    clear: () => setFilterBrand('') },
              { key: 'platform', value: filterPlatform, clear: () => setFilterPlatform('') },
            ].filter(f => f.value).map(f => (
              <span key={f.key} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 bg-salty-purple/10 border border-salty-purple/25 text-salty-purple rounded-full text-xs font-semibold capitalize">
                {f.value}
                <button onClick={f.clear} className="hover:text-white transition-colors rounded-full">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <button
              onClick={() => { setFilterTier(''); setFilterCategory(''); setFilterStatus(''); setFilterSource(''); setFilterBrand(''); setFilterPlatform(''); setFilterPoc(''); }}
              className="text-[10px] text-salty-text-muted hover:text-salty-rose transition-colors font-semibold ml-1"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-salty-border grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="filters-panel">
            {[
              { label: 'Tier', value: filterTier, onChange: setFilterTier, options: TIERS, fmt: t => t.charAt(0).toUpperCase() + t.slice(1), testId: 'filter-tier' },
              { label: 'Category', value: filterCategory, onChange: setFilterCategory, options: CATEGORIES, fmt: c => `${CATEGORY_EMOJI[c] || ''} ${c.charAt(0).toUpperCase() + c.slice(1)}`, testId: 'filter-category' },
              { label: 'Status', value: filterStatus, onChange: setFilterStatus, options: STATUSES, fmt: s => s.charAt(0).toUpperCase() + s.slice(1), testId: 'filter-status' },
              { label: 'Source', value: filterSource, onChange: setFilterSource, options: SOURCES, fmt: s => s === 'pr' ? 'PR' : s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-') },
              { label: 'Works For', value: filterBrand, onChange: setFilterBrand, options: BRANDS, fmt: b => b.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') },
              { label: 'Platform', value: filterPlatform, onChange: setFilterPlatform, options: ['instagram', 'youtube', 'both'], fmt: p => p === 'both' ? 'IG + YouTube' : p.charAt(0).toUpperCase() + p.slice(1), testId: 'filter-platform' },
            ].map(({ label, value, onChange, options, fmt, testId }) => (
              <div key={label}>
                <label className="text-[10px] font-bold uppercase tracking-wider text-salty-text-muted mb-1.5 block">{label}</label>
                <select
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-sm text-salty-text-main focus:ring-1 focus:ring-salty-purple focus:border-salty-purple outline-none transition-colors"
                  data-testid={testId}
                >
                  <option value="">All {label}s</option>
                  {options.map(o => <option key={o} value={o}>{fmt(o)}</option>)}
                </select>
              </div>
            ))}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-salty-text-muted mb-1.5 block">POC Name</label>
              <Input
                type="text"
                value={filterPoc}
                onChange={(e) => setFilterPoc(e.target.value)}
                placeholder="Filter by POC..."
                className="w-full h-[38px] bg-salty-bg border-salty-border text-sm text-salty-text-main placeholder:text-slate-500 focus:ring-1 focus:ring-salty-purple focus:border-salty-purple outline-none transition-colors"
                data-testid="filter-poc"
              />
            </div>
          </div>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-salty-card border border-salty-purple/40 shadow-2xl shadow-salty-purple/20 rounded-2xl px-5 py-3">
          <span className="text-sm font-bold text-white mr-1">{selectedIds.size} selected</span>
          <div className="w-px h-5 bg-salty-border" />
          <button
            onClick={handleBulkSync}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-salty-teal/10 hover:bg-salty-teal/20 text-salty-teal text-xs font-bold transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            Sync Metrics
          </button>
          <div className="relative group">
            <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-salty-amber/10 hover:bg-salty-amber/20 text-salty-amber text-xs font-bold transition-colors">
              <Check className="w-3.5 h-3.5" />
              Set Status
              <ChevronDown className="w-3 h-3" />
            </button>
            <div className="absolute bottom-full mb-2 left-0 hidden group-hover:flex flex-col bg-salty-card border border-salty-border rounded-xl shadow-xl overflow-hidden min-w-[130px]">
              {STATUSES.map(s => (
                <button
                  key={s}
                  onClick={() => handleBulkStatusChange(s)}
                  className="px-4 py-2 text-xs text-left text-salty-text-main hover:bg-salty-bg transition-colors capitalize"
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-salty-rose/10 hover:bg-salty-rose/20 text-salty-rose text-xs font-bold transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
          <div className="w-px h-5 bg-salty-border" />
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-salty-text-muted hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Influencers Table */}
      <div className="bg-salty-card border border-salty-border rounded-xl overflow-hidden" data-testid="influencers-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-salty-subtle">
              <tr>
                {userRole !== 'viewer' && (
                  <th className="py-4 pl-4 pr-2 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-salty-border bg-salty-bg accent-salty-purple cursor-pointer"
                    />
                  </th>
                )}
                 <th onClick={() => requestSort('name')} className="text-left py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider cursor-pointer group hover:bg-salty-subtle/50 transition-colors">
                  <div className="flex items-center gap-1">Influencer <SortIndicator column="name" /></div>
                </th>
                <th className="text-left py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider">Contact</th>
                <th onClick={() => requestSort('poc')} className="text-left py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider cursor-pointer group hover:bg-salty-subtle/50 transition-colors">
                  <div className="flex items-center gap-1">POC <SortIndicator column="poc" /></div>
                </th>
                <th onClick={() => requestSort('tier')} className="text-left py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider cursor-pointer group hover:bg-salty-subtle/50 transition-colors">
                  <div className="flex items-center gap-1">Tier / Category <SortIndicator column="tier" /></div>
                </th>
                <th onClick={() => requestSort('followers_count')} className="text-left py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider cursor-pointer group hover:bg-salty-subtle/50 transition-colors">
                  <div className="flex items-center gap-1">Followers <SortIndicator column="followers_count" /></div>
                </th>
                <th onClick={() => requestSort('growthScore')} className="text-left py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider cursor-pointer group hover:bg-salty-subtle/50 transition-colors">
                  <div className="flex items-center gap-1">Growth (7d) <SortIndicator column="growthScore" /></div>
                </th>
                <th onClick={() => requestSort('performanceScore')} className="text-left py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider cursor-pointer group hover:bg-salty-subtle/50 transition-colors">
                  <div className="flex items-center gap-1">Ad Performance <SortIndicator column="performanceScore" /></div>
                </th>
                <th onClick={() => requestSort('cpvScore')} className="text-left py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider cursor-pointer group hover:bg-salty-subtle/50 transition-colors">
                  <div className="flex items-center gap-1">CPV <SortIndicator column="cpvScore" /></div>
                </th>
                <th className="text-left py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider">Status</th>
                <th className="text-right py-4 px-4 font-outfit font-bold text-[10px] text-salty-text-muted uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="py-8 text-center text-slate-400">
                    <div className="animate-pulse">Loading influencers...</div>
                  </td>
                </tr>
              ) : pagedInfluencers.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-8 text-center text-slate-400">
                    <div className="flex flex-col items-center gap-2">
                      <Users className="w-12 h-12 opacity-50" />
                      <p>No influencers found</p>
                      <Button
                        size="sm"
                        onClick={handleSeedData}
                        className="mt-2 bg-salty-purple hover:bg-salty-purple-light"
                      >
                        Import from Excel
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                pagedInfluencers.map((influencer) => {
                  const handle = influencer.instagram_handle?.replace(/^@+/g, '').toLowerCase();
                  const tracker = allTrackers[handle];
                  const trackerAds = tracker?.trackedAds || [];
                  const accountEr = parseFloat(tracker?.computed?.engagementRate) || influencer.engagement_rate || 1;
                  const avgVis = parseFloat(tracker?.computed?.avgReelViews) || 1;
                  const followers = tracker?.profile?.followers || influencer.followers_count || 1;

                  // Campaign posts for this influencer (synced via campaign sync)
                  const campPosts = (campaignPostsByHandle[handle] || [])
                    .filter(p => p.synced_at) // only count posts that have been synced
                    .map(p => ({
                      likes: p.likes || 0,
                      comments: p.comments || 0,
                      reelViews: p.views || 0,
                      format: 'Reel',
                      _source: 'campaign',
                      campaign_name: p.campaign_name,
                    }));

                  // Merge: tracker ads take precedence; fill gaps with campaign posts not already tracked
                  const trackedUrls = new Set(trackerAds.map(a => a.url));
                  const extraCampPosts = campPosts.filter(p => !trackedUrls.has(p.url));
                  const trackedAds = [...trackerAds, ...extraCampPosts];

                  let avgPerformance = null;
                  let avgMetrics = { likes: 0, comments: 0, views: 0 };

                  if (trackedAds.length > 0) {
                    const totalMult = trackedAds.reduce((acc, ad) => {
                      const isReel = ad.format === 'Reel';
                      const performance = (isReel && ad.reelViews > 0 && avgVis > 1)
                        ? (ad.reelViews / avgVis)
                        : ((ad.likes + ad.comments) / followers * 100) / (accountEr || 1);

                      avgMetrics.likes    += ad.likes || 0;
                      avgMetrics.comments += ad.comments || 0;
                      avgMetrics.views    += ad.reelViews || 0;

                      return acc + performance;
                    }, 0);

                    avgPerformance = (totalMult / trackedAds.length).toFixed(1);
                    avgMetrics.likes    = Math.round(avgMetrics.likes    / trackedAds.length);
                    avgMetrics.comments = Math.round(avgMetrics.comments / trackedAds.length);
                    avgMetrics.views    = Math.round(avgMetrics.views    / trackedAds.length);
                  }

                  return (
                    <tr
                      key={influencer.id}
                      className={`border-b border-salty-border hover:bg-salty-bg/30 transition-colors ${selectedIds.has(influencer.id) ? 'bg-salty-purple/5' : ''}`}
                      data-testid={`influencer-row-${influencer.id}`}
                    >
                      {userRole !== 'viewer' && (
                        <td className="py-3 pl-4 pr-2 w-8">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(influencer.id)}
                            onChange={() => toggleSelect(influencer.id)}
                            className="w-4 h-4 rounded border-salty-border bg-salty-bg accent-salty-purple cursor-pointer"
                            onClick={e => e.stopPropagation()}
                          />
                        </td>
                      )}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-salty-purple/20 flex items-center justify-center text-salty-purple font-semibold border border-salty-border flex-shrink-0">
                            {(() => {
                              const pic = influencer.instagram_profile_pic
                                || tracker?.profile?.profilePicUrl
                                || influencer.youtube_profile_pic;
                              const isYtPic = !influencer.instagram_profile_pic && !tracker?.profile?.profilePicUrl && influencer.youtube_profile_pic;
                              if (pic) return (
                                <img
                                  src={isYtPic ? pic : getProxiedImage(pic)}
                                  alt={influencer.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.parentElement.innerHTML = influencer.name?.charAt(0).toUpperCase() || '?';
                                  }}
                                />
                              );
                              return influencer.name?.charAt(0)?.toUpperCase() || '?';
                            })()}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-salty-text-main font-bold text-sm">{influencer.name}</p>
                              {influencer.platform === 'youtube' && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400">
                                  <Youtube className="w-2.5 h-2.5" />YT
                                </span>
                              )}
                              {influencer.platform === 'both' && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded bg-salty-purple/10 border border-salty-purple/20 text-salty-purple">
                                  IG+YT
                                </span>
                              )}
                            </div>
                            {influencer.instagram_handle && (
                              <a
                                href={`https://instagram.com/${influencer.instagram_handle.replace(/^@+/, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-salty-text-muted flex items-center gap-1 hover:text-salty-purple transition-colors"
                              >
                                <Instagram className="w-3 h-3" />
                                @{influencer.instagram_handle.replace(/^@+/, '')}
                              </a>
                            )}
                            {influencer.youtube_handle && (
                              <a
                                href={`https://youtube.com/@${influencer.youtube_handle.replace(/^@+/, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-red-400/70 flex items-center gap-1 hover:text-red-400 transition-colors"
                              >
                                <Youtube className="w-3 h-3" />
                                @{influencer.youtube_handle.replace(/^@+/, '')}
                                {influencer.youtube_subscribers > 0 && (
                                  <span className="text-slate-500">· {formatNumber(influencer.youtube_subscribers)} subs</span>
                                )}
                              </a>
                            )}
                          {influencer.works_for && (
                              <span className="mt-1 inline-flex text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-salty-purple/10 border border-salty-purple/20 text-salty-purple">
                                {influencer.works_for}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          {influencer.phone && (
                            <p className="text-xs text-salty-text-muted flex items-center gap-1 font-medium">
                              <Phone className="w-3 h-3 flex-shrink-0" />
                              {influencer.phone}
                            </p>
                          )}
                          {influencer.email && (
                            <p className="text-xs text-salty-text-muted flex items-center gap-1 font-medium">
                              <Mail className="w-3 h-3 flex-shrink-0" />
                              {influencer.email}
                            </p>
                          )}
                          {!influencer.phone && !influencer.email && (
                            <span className="text-salty-text-muted text-[11px]">—</span>
                          )}
                        </div>
                      </td>

                      {/* POC */}
                      <td className="py-3 px-4">
                        {influencer.poc ? (
                          <div className="flex items-center gap-1.5 text-xs text-salty-text-main font-medium">
                            <span className="w-5 h-5 rounded-full bg-salty-purple/10 text-salty-purple flex items-center justify-center text-[10px] font-bold shrink-0">
                              {influencer.poc.charAt(0).toUpperCase()}
                            </span>
                            <span className="truncate">{influencer.poc}</span>
                          </div>
                        ) : (
                          <span className="text-salty-text-muted/40">—</span>
                        )}
                      </td>

                      {/* Tier */}
                      {/* Tier + Category (combined) */}
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1.5">
                          {(() => {
                            const tier = (influencer.tier || 'micro').toLowerCase();
                            const cfg = {
                              mega:  'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/25',
                              macro: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/25',
                              mid:   'bg-teal-500/15 text-teal-600 dark:text-teal-400 border-teal-500/25',
                              micro: 'bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/25',
                              nano:  'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/25',
                            }[tier] || 'bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/25';
                            const cat = (influencer.category || 'lifestyle').toLowerCase();
                            return (<>
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border w-fit ${cfg}`}>
                                {tier}
                              </span>
                              <span className="inline-flex items-center gap-1 text-[11px] text-salty-text-muted font-medium capitalize">
                                <span>{CATEGORY_EMOJI[cat] || '🌟'}</span>{cat}
                              </span>
                            </>);
                          })()}
                        </div>
                      </td>

                      {/* Followers / Subscribers + ER */}
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                          {(() => {
                            const isYt = influencer.platform === 'youtube';
                            const isBoth = influencer.platform === 'both';
                            return (<>
                              {(isYt || isBoth) && influencer.youtube_subscribers > 0 && (
                                <div className="flex items-center gap-1">
                                  <Youtube className="w-3 h-3 text-red-400 shrink-0" />
                                  <span className="text-salty-text-main font-bold font-mono text-sm">{formatNumber(influencer.youtube_subscribers)}</span>
                                </div>
                              )}
                              {(!isYt || isBoth) && (
                                <div className="flex items-center gap-1">
                                  {isBoth && <Instagram className="w-3 h-3 text-pink-400 shrink-0" />}
                                  <span className="text-salty-text-main font-bold font-mono text-sm">{formatNumber(influencer.followers_count || 0)}</span>
                                </div>
                              )}
                            </>);
                          })()}
                          {(() => {
                            const er = parseFloat(influencer.engagement_rate);
                            const badge = getErBadge(er);
                            if (!badge) return null;
                            return (
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold w-fit ${badge.classes}`}>
                                {badge.emoji} {er.toFixed(1)}% ER
                              </span>
                            );
                          })()}
                        </div>
                      </td>

                      {/* Growth (7d) */}
                      <td className="py-3 px-4">
                        {(() => {
                          const history = tracker?.history || [];
                          const validHistory = history.filter(h => h.followers > 0);
                          const lastSync = influencer.metrics_last_synced || influencer.social_metrics?.last_synced || history[history.length - 1]?.timestamp;
                          const rel = getRelativeTime(lastSync);
                          let diff = 0, isPos = true;
                          if (validHistory.length >= 2) {
                            const latest = validHistory[validHistory.length - 1];
                            const prev   = validHistory[validHistory.length - 2];
                            diff  = latest.followers - prev.followers;
                            isPos = diff >= 0;
                          }
                          return (
                            <div className="space-y-1 min-w-[80px]">
                              {validHistory.length >= 2 ? (
                                <div className="flex items-end gap-2">
                                  <Sparkline history={validHistory} positive={isPos} />
                                  <span className={`text-[11px] font-bold leading-none pb-0.5 ${isPos ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                    {isPos ? '+' : ''}{formatNumber(diff)}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-salty-text-muted text-[11px]">—</span>
                              )}
                              {rel && (
                                <div className={`flex items-center gap-1 text-[9px] font-semibold ${rel.stale ? 'text-rose-500 dark:text-rose-400' : 'text-salty-text-muted'}`} title={lastSync ? new Date(lastSync).toLocaleString() : ''}>
                                  <Clock className="w-2.5 h-2.5" />
                                  {rel.stale ? '⚠ ' : ''}{rel.text}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>

                      {/* Ad Performance */}
                      <td className="py-3 px-4">
                        {avgPerformance ? (
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-black w-fit border ${
                                parseFloat(avgPerformance) >= 1.2 ? 'bg-salty-teal/10 text-salty-teal border-salty-teal/20' :
                                parseFloat(avgPerformance) >= 0.8 ? 'bg-salty-amber/10 text-salty-amber border-salty-amber/20' :
                                'bg-salty-rose/10 text-salty-rose border-salty-rose/20'
                              }`}>
                                {avgPerformance}x Avg
                              </span>
                              <span className="text-[10px] text-salty-text-muted font-bold uppercase tracking-wider">
                                {trackedAds.length} {trackedAds.length === 1 ? 'Ad' : 'Ads'}
                                {extraCampPosts.length > 0 && trackerAds.length === 0 && (
                                  <span className="ml-1 text-salty-purple/70 normal-case tracking-normal font-medium">(campaign)</span>
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-x-3 whitespace-nowrap">
                              <div className="flex items-center gap-1 text-xs text-salty-text-muted font-mono">
                                <span className="font-bold text-salty-text-main">{formatNumber(avgMetrics.views)}</span>
                                <span className="text-[10px] uppercase tracking-tighter opacity-70">Views</span>
                              </div>
                              <div className="flex items-center gap-1 text-xs text-salty-text-muted font-mono">
                                <span className="font-bold text-salty-text-main">{formatNumber(avgMetrics.likes)}</span>
                                <span className="text-[10px] uppercase tracking-tighter opacity-70">Likes</span>
                              </div>
                              <div className="flex items-center gap-1 text-xs text-salty-text-muted font-mono">
                                <span className="font-bold text-salty-text-main">{formatNumber(avgMetrics.comments)}</span>
                                <span className="text-[10px] uppercase tracking-tighter opacity-70">Cmnts</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-salty-text-muted text-[11px]">—</span>
                        )}
                      </td>

                      {/* CPV */}
                      <td className="py-3 px-4">
                        {influencer.cpvScore != null ? (() => {
                          const cpv = influencer.cpvScore;
                          const color = cpv < 0.5 ? 'text-emerald-500' : cpv < 2 ? 'text-salty-text-main' : 'text-amber-500';
                          const label = cpv < 0.5 ? '↑ great' : cpv < 2 ? 'good' : '↓ high';
                          return (
                            <div className="flex flex-col">
                              <span className={`text-sm font-bold font-mono tabular-nums ${color}`}>
                                ₹{cpv < 1 ? cpv.toFixed(2) : cpv.toFixed(1)}
                              </span>
                              <span className={`text-[10px] font-semibold ${color}`}>{label}</span>
                            </div>
                          );
                        })() : <span className="text-salty-text-muted/40 text-[11px]">—</span>}
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4">
                        {(() => {
                          const s = (influencer.status || 'active').toLowerCase();
                          const cfg = {
                            active:      { dot: 'bg-emerald-400', classes: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
                            inactive:    { dot: 'bg-slate-400',   classes: 'bg-slate-500/10 text-slate-500 dark:text-slate-400 border-slate-500/20' },
                            'on hold':   { dot: 'bg-amber-400',   classes: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
                            blacklisted: { dot: 'bg-rose-500',    classes: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20' },
                          }[s] || { dot: 'bg-slate-400', classes: 'bg-slate-500/10 text-slate-400 border-slate-500/20' };
                          return (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border capitalize ${cfg.classes}`}>
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                              {s}
                            </span>
                          );
                        })()}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4">
                        <div className="flex justify-end items-center gap-1">
                          {/* Sync */}
                          {userRole !== 'viewer' && (
                            <Button size="sm" variant="ghost"
                              onClick={() => handleSyncSingleMetrics(influencer)}
                              disabled={syncingId === influencer.id}
                              className="w-8 h-8 p-0 text-salty-text-muted hover:text-salty-teal hover:bg-salty-teal/10 rounded-lg"
                              title="Sync metrics"
                              data-testid={`sync-influencer-${influencer.id}`}
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${syncingId === influencer.id ? 'animate-spin' : ''}`} />
                            </Button>
                          )}
                          {/* Live tracker — Instagram */}
                          {influencer.instagram_handle && (
                            <Button size="sm" variant="ghost"
                              onClick={() => setTrackingHandle(influencer.instagram_handle)}
                              className="w-8 h-8 p-0 text-salty-text-muted hover:text-salty-purple hover:bg-salty-purple/10 rounded-lg"
                              title="Live Instagram analytics"
                            >
                              <Activity className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {/* YouTube analytics tracker */}
                          {influencer.youtube_handle && (
                            <Button size="sm" variant="ghost"
                              onClick={() => setYtTrackerInfluencer(influencer)}
                              className="w-8 h-8 p-0 text-salty-text-muted hover:text-red-400 hover:bg-red-400/10 rounded-lg"
                              title="YouTube analytics"
                            >
                              <Youtube className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {/* Edit */}
                          {userRole !== 'viewer' && (
                            <Button size="sm" variant="ghost"
                              onClick={() => { setSelectedInfluencer(influencer); setShowEditModal(true); }}
                              className="w-8 h-8 p-0 text-salty-text-muted hover:text-salty-purple hover:bg-salty-purple/10 rounded-lg"
                              title="Edit influencer"
                              data-testid={`edit-influencer-${influencer.id}`}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {/* Add to campaign */}
                          {userRole !== 'viewer' && (
                            <Button size="sm" variant="ghost"
                              onClick={() => { setSelectedInfluencer(influencer); setShowAddToCampaignModal(true); }}
                              className="w-8 h-8 p-0 text-salty-text-muted hover:text-amber-400 hover:bg-amber-400/10 rounded-lg"
                              title="Add to campaign"
                            >
                              <Megaphone className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {/* Delete */}
                          {userRole !== 'viewer' && (
                            <Button size="sm" variant="ghost"
                              onClick={() => handleDelete(influencer.id)}
                              className="w-8 h-8 p-0 text-salty-text-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-lg"
                              title="Delete influencer"
                              data-testid={`delete-influencer-${influencer.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Modern Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-salty-border">
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-400">
              {selectedIds.size > 0
                ? <span className="text-salty-purple font-bold">{selectedIds.size} selected · </span>
                : null}
              {pageSize === 0
                ? `Showing all ${allSortedIds.length}`
                : `${Math.min(currentPage * pageSize + 1, allSortedIds.length)}–${Math.min((currentPage + 1) * pageSize, allSortedIds.length)} of ${allSortedIds.length}`}
              {allSortedIds.length !== allInfluencers.length && ` (filtered from ${allInfluencers.length})`}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>Rows</span>
              {[25, 50, 100, 0].map(n => (
                <button
                  key={n}
                  onClick={() => { setPageSize(n); setCurrentPage(0); }}
                  className={`px-2 py-1 rounded font-bold transition-colors ${pageSize === n ? 'bg-salty-purple text-white' : 'hover:bg-salty-bg text-slate-400 hover:text-white'}`}
                >
                  {n === 0 ? 'All' : n}
                </button>
              ))}
            </div>
            {pageSize > 0 && totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(0)}
                  disabled={currentPage === 0}
                  className="p-1 rounded hover:bg-salty-bg text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                >«</button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="p-1 rounded hover:bg-salty-bg text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                ><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-xs text-slate-400 px-2 tabular-nums">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="p-1 rounded hover:bg-salty-bg text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                ><ChevronRight className="w-4 h-4" /></button>
                <button
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="p-1 rounded hover:bg-salty-bg text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                >»</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <InfluencerModal
          influencer={selectedInfluencer}
          isEdit={showEditModal}
          onClose={() => { setShowAddModal(false); setShowEditModal(false); setSelectedInfluencer(null); }}
          onSave={() => { fetchInfluencers(); fetchStats(); }}
        />
      )}

      {/* Live Tracker Modal */}
      {ytTrackerInfluencer && (
        <YouTubeTrackerModal
          influencer={ytTrackerInfluencer}
          onClose={() => setYtTrackerInfluencer(null)}
          onSyncComplete={() => { fetchInfluencers(); fetchStats(); }}
        />
      )}

      {trackingHandle && (
        <TrackerModal
          instagramHandle={trackingHandle}
          initialData={allTrackers[trackingHandle.replace(/^@+/, '').toLowerCase()] || null}
          ratePerReel={(() => { const inf = allInfluencers.find(i => i.instagram_handle?.replace(/^@+/, '').toLowerCase() === trackingHandle.replace(/^@+/, '').toLowerCase()); return inf?.commercials?.rate_per_reel || 0; })()}
          campaignPosts={campaignPostsByHandle[trackingHandle.replace(/^@+/, '').toLowerCase()] || []}
          onClose={() => setTrackingHandle(null)}
          onTrackerUpdated={(username, data) => {
            setAllTrackers(prev => ({ ...prev, [username]: data }));
          }}
          onSyncComplete={async (metrics) => {
            try {
              const handle = trackingHandle.replace(/^@+/, '');
              const inf = allInfluencers.find(i => i.instagram_handle?.replace(/^@+/, '') === handle);
              if (inf) {
                await api.updateInfluencer(inf.id, {
                  followers_count: metrics.followers,
                  engagement_rate: metrics.engagement_rate,
                  tier: calculateTier(metrics.followers),
                  instagram_profile_pic: metrics.profilePicUrl || '',
                  metrics_last_synced: new Date().toISOString()
                });
                fetchInfluencers();
                fetchStats();
              }
            } catch (err) {
              console.error('Failed to sync deep metrics to DB', err);
            }
          }}
        />
      )}

      {showImportModal && (
        <ImportModal 
          onClose={() => setShowImportModal(false)} 
          onSuccess={() => fetchInfluencers()} 
        />
      )}

      {showAdImportModal && (
        <AdImportModal
          influencers={allInfluencers}
          onClose={() => { setShowAdImportModal(false); loadAllTrackerData(); }}
        />
      )}

      {showAddToCampaignModal && (
        <AddToCampaignModal 
          influencer={selectedInfluencer}
          onClose={() => { setShowAddToCampaignModal(false); setSelectedInfluencer(null); }}
        />
      )}
    </div>
  );
};

// Influencer Add/Edit Modal Component
const InfluencerModal = ({ influencer, isEdit, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fetchingYt, setFetchingYt] = useState(false);

  const initPlatform = () => {
    if (!influencer) return 'instagram';
    if (influencer.platform) return influencer.platform;
    if (influencer.youtube_handle || influencer.youtube_channel) {
      return influencer.instagram_handle ? 'both' : 'youtube';
    }
    return 'instagram';
  };

  const [platform, setPlatform] = useState(initPlatform());
  const [formData, setFormData] = useState({
    name: influencer?.name || '',
    email: influencer?.email || '',
    phone: influencer?.phone || '',
    instagram_handle: influencer?.instagram_handle || '',
    youtube_handle: influencer?.youtube_handle || influencer?.youtube_channel || '',
    youtube_channel_id: influencer?.youtube_channel_id || '',
    youtube_channel_name: influencer?.youtube_channel_name || '',
    youtube_profile_pic: influencer?.youtube_profile_pic || '',
    youtube_subscribers: influencer?.youtube_subscribers || 0,
    youtube_avg_views: influencer?.youtube_avg_views || 0,
    youtube_total_views: influencer?.youtube_total_views || 0,
    youtube_video_count: influencer?.youtube_video_count || 0,
    address: influencer?.address || '',
    city: influencer?.city || '',
    state: influencer?.state || '',
    category: influencer?.category || 'lifestyle',
    tier: influencer?.tier || 'micro',
    followers_count: influencer?.followers_count || 0,
    engagement_rate: influencer?.engagement_rate || 0,
    rate_per_post: influencer?.commercials?.rate_per_post || 0,
    rate_per_reel: influencer?.commercials?.rate_per_reel || 0,
    rate_per_story: influencer?.commercials?.rate_per_story || 0,
    rate_per_video: influencer?.commercials?.rate_per_video || 0,
    barter_only: influencer?.commercials?.barter_only || false,
    notes: influencer?.notes || '',
    status: influencer?.status || 'active',
    source: influencer?.source || '',
    works_for: influencer?.works_for || '',
    poc: influencer?.poc || '',
  });

  const showInstagram = platform === 'instagram' || platform === 'both';
  const showYouTube   = platform === 'youtube'   || platform === 'both';

  const handleFetchInstagramData = async () => {
    if (!formData.instagram_handle) { toast.error('Enter an Instagram handle first'); return; }
    const username = formData.instagram_handle.replace('@', '').trim().toLowerCase();
    try {
      setFetching(true);
      toast.info(`Fetching data for @${username}...`);
      const data = await fetchInfluencer(username);
      const followers = data.profile.followers || 0;
      const er = parseFloat(data.computed.engagementRate) || 0;
      const category = data.computed.primaryCategory?.toLowerCase() || 'lifestyle';
      setFormData(prev => ({
        ...prev,
        name: data.profile.fullName || prev.name,
        followers_count: followers,
        engagement_rate: er,
        tier: calculateTier(followers),
        category: CATEGORIES.includes(category) ? category : 'lifestyle',
        status: 'active',
      }));
      toast.success('Instagram data fetched!');
    } catch (err) {
      toast.error(err.message === 'NO_API_KEY' ? 'Please configure API Key' : 'Failed to fetch Instagram data');
    } finally {
      setFetching(false);
    }
  };

  const handleFetchYouTubeData = async () => {
    if (!formData.youtube_handle) { toast.error('Enter a YouTube handle or URL first'); return; }
    try {
      setFetchingYt(true);
      toast.info('Fetching YouTube channel data...');
      const res = await api.fetchYouTubeChannel(formData.youtube_handle);
      const ch = res.data;
      setFormData(prev => ({
        ...prev,
        name: prev.name || ch.name,
        youtube_channel_id: ch.channel_id,
        youtube_channel_name: ch.name,
        youtube_profile_pic: ch.profile_pic || '',
        youtube_subscribers: ch.subscribers,
        youtube_avg_views: ch.avg_views_per_video,
        youtube_total_views: ch.total_views,
        youtube_video_count: ch.video_count,
      }));
      toast.success(`Found: ${ch.name} · ${formatNumber(ch.subscribers)} subscribers`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to fetch YouTube data';
      toast.error(msg);
    } finally {
      setFetchingYt(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    try {
      setSaving(true);
      const cleanedData = {
        ...formData,
        platform,
        instagram_handle: formData.instagram_handle.replace(/^@+/, '').trim().toLowerCase(),
        youtube_handle: formData.youtube_handle.replace(/^@+/, '').trim().toLowerCase(),
      };
      if (isEdit) {
        await api.updateInfluencer(influencer.id, cleanedData);
        toast.success('Influencer updated');
      } else {
        await api.createInfluencer(cleanedData);
        toast.success('Influencer added');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save influencer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="influencer-modal">
      <div className="bg-salty-card border border-salty-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-4 border-b border-salty-border">
          <h2 className="text-xl font-outfit font-semibold text-white">
            {isEdit ? 'Edit Influencer' : 'Add New Influencer'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-5">

          {/* Platform selector */}
          <div>
            <label className="text-xs text-slate-400 mb-2 block">Platform</label>
            <div className="flex gap-2">
              {[
                { key: 'instagram', label: 'Instagram', icon: <Instagram className="w-3.5 h-3.5" />, active: 'bg-pink-600 border-transparent text-white' },
                { key: 'youtube',   label: 'YouTube',   icon: <Youtube   className="w-3.5 h-3.5" />, active: 'bg-red-600 border-transparent text-white' },
                { key: 'both',      label: 'Both',      icon: null,                                   active: 'bg-salty-purple border-transparent text-white' },
              ].map(({ key, label, icon, active }) => (
                <button key={key} type="button" onClick={() => setPlatform(key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                    platform === key ? active : 'bg-salty-bg border-salty-border text-slate-400 hover:border-slate-500'
                  }`}>
                  {icon}{label}
                </button>
              ))}
            </div>
          </div>

          {/* Instagram section */}
          {showInstagram && (
            <div className="rounded-xl border border-pink-500/20 bg-pink-500/[0.04] p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Instagram className="w-4 h-4 text-pink-400" />
                <span className="text-sm font-semibold text-pink-300">Instagram</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs text-slate-400 mb-1 block">Handle</label>
                  <div className="flex gap-2">
                    <Input name="instagram_handle" value={formData.instagram_handle} onChange={handleChange}
                      className="bg-salty-bg border-salty-border text-white" placeholder="@username" />
                    <Button type="button" size="sm" onClick={handleFetchInstagramData} disabled={fetching}
                      className="bg-pink-600 hover:bg-pink-700 text-white shrink-0">
                      {fetching ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Fetch'}
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Followers</label>
                  <Input value={formatNumber(formData.followers_count)} className="bg-salty-bg border-salty-border text-white opacity-60 cursor-not-allowed" disabled />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Engagement Rate</label>
                  <Input value={formData.engagement_rate + '%'} className="bg-salty-bg border-salty-border text-white opacity-60 cursor-not-allowed" disabled />
                </div>
              </div>
            </div>
          )}

          {/* YouTube section */}
          {showYouTube && (
            <div className="rounded-xl border border-red-500/20 bg-red-500/[0.04] p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Youtube className="w-4 h-4 text-red-400" />
                <span className="text-sm font-semibold text-red-300">YouTube</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs text-slate-400 mb-1 block">Channel Handle or URL</label>
                  <div className="flex gap-2">
                    <Input name="youtube_handle" value={formData.youtube_handle} onChange={handleChange}
                      className="bg-salty-bg border-salty-border text-white" placeholder="@handle or youtube.com/@channel" />
                    <Button type="button" size="sm" onClick={handleFetchYouTubeData} disabled={fetchingYt}
                      className="bg-red-600 hover:bg-red-700 text-white shrink-0">
                      {fetchingYt ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Fetch'}
                    </Button>
                  </div>
                </div>
                {formData.youtube_channel_name && (
                  <div className="md:col-span-2 flex items-center gap-3 p-2 rounded-lg bg-salty-bg/50 border border-salty-border/50">
                    {formData.youtube_profile_pic && (
                      <img src={formData.youtube_profile_pic} alt="" className="w-8 h-8 rounded-full object-cover" />
                    )}
                    <div>
                      <p className="text-sm font-semibold text-white">{formData.youtube_channel_name}</p>
                      <p className="text-xs text-emerald-400">Channel verified ✓</p>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Subscribers</label>
                  <Input value={formatNumber(formData.youtube_subscribers)} className="bg-salty-bg border-salty-border text-white opacity-60 cursor-not-allowed" disabled />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Avg Views / Video</label>
                  <Input value={formatNumber(formData.youtube_avg_views)} className="bg-salty-bg border-salty-border text-white opacity-60 cursor-not-allowed" disabled />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Total Videos</label>
                  <Input value={formatNumber(formData.youtube_video_count)} className="bg-salty-bg border-salty-border text-white opacity-60 cursor-not-allowed" disabled />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Total Channel Views</label>
                  <Input value={formatNumber(formData.youtube_total_views)} className="bg-salty-bg border-salty-border text-white opacity-60 cursor-not-allowed" disabled />
                </div>
              </div>
            </div>
          )}

          {/* Name / Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Name *</label>
              <Input name="name" value={formData.name} onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white" placeholder="Full name" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Email</label>
              <Input name="email" type="email" value={formData.email} onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white" placeholder="email@example.com" data-testid="input-email" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Phone</label>
              <Input name="phone" value={formData.phone} onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white" placeholder="Phone number" data-testid="input-phone" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Relationship Status</label>
              <select name="status" value={formData.status} onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-white">
                {STATUSES.map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Classification */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Tier (Auto)</label>
              <Input value={formData.tier.charAt(0).toUpperCase() + formData.tier.slice(1)}
                className="bg-salty-bg border-salty-border text-white opacity-60 cursor-not-allowed" disabled />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Category</label>
              <select name="category" value={formData.category} onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-white">
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Source</label>
              <select name="source" value={formData.source} onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-white">
                <option value="">Select source...</option>
                {SOURCES.map(s => (
                  <option key={s} value={s}>{s === 'pr' ? 'PR' : s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-')}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Works For</label>
              <select name="works_for" value={formData.works_for} onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-white">
                <option value="">Select brand...</option>
                {BRANDS.map(b => (
                  <option key={b} value={b}>{b.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">POC Name</label>
              <Input name="poc" value={formData.poc} onChange={handleChange}
                className="bg-salty-bg border-salty-border text-white" placeholder="POC Name" data-testid="input-poc" />
            </div>
          </div>

          {/* Commercials */}
          <div className="border-t border-salty-border pt-4">
            <h3 className="text-sm font-semibold text-white mb-3">Commercial Rates (₹)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {showInstagram && (<>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Per Post</label>
                  <Input name="rate_per_post" type="number" value={formData.rate_per_post} onChange={handleChange}
                    className="bg-salty-bg border-salty-border text-white" data-testid="input-rate-post" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Per Reel</label>
                  <Input name="rate_per_reel" type="number" value={formData.rate_per_reel} onChange={handleChange}
                    className="bg-salty-bg border-salty-border text-white" data-testid="input-rate-reel" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Per Story</label>
                  <Input name="rate_per_story" type="number" value={formData.rate_per_story} onChange={handleChange}
                    className="bg-salty-bg border-salty-border text-white" data-testid="input-rate-story" />
                </div>
              </>)}
              {showYouTube && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Per Video</label>
                  <Input name="rate_per_video" type="number" value={formData.rate_per_video} onChange={handleChange}
                    className="bg-salty-bg border-salty-border text-white" />
                </div>
              )}
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" name="barter_only" checked={formData.barter_only} onChange={handleChange}
                    className="w-4 h-4 rounded border-salty-border bg-salty-bg" data-testid="checkbox-barter" />
                  Barter Only
                </label>
              </div>
            </div>
          </div>

          {/* Address & Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Address</label>
              <textarea name="address" value={formData.address} onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-white resize-none"
                rows={3} placeholder="Full address" data-testid="input-address" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Internal Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange}
                className="w-full bg-salty-bg border border-salty-border rounded-lg px-3 py-2 text-white resize-none"
                rows={3} placeholder="Private notes..." data-testid="input-notes" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-salty-border">
            <Button type="button" variant="outline" onClick={onClose} className="border-salty-border text-slate-300">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-salty-purple hover:bg-salty-purple-light" data-testid="save-influencer-btn">
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Add Influencer')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Modal for adding an influencer to a campaign
const AddToCampaignModal = ({ influencer, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const res = await api.getInfluencerCampaigns({ status: ['active', 'draft'] });
        setCampaigns(res.data.campaigns || []);
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        toast.error('Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  const handleAdd = async (campaignId) => {
    try {
      setSaving(campaignId);
      await api.addInfluencerToCampaign(campaignId, {
        influencer_id: influencer.id,
        agreed_fee: influencer.commercials?.rate_per_reel || 0,
        notes: `Added from database on ${new Date().toLocaleDateString()}`
      });
      toast.success(`Successfully added @${influencer.instagram_handle} to campaign`);
      onClose();
    } catch (err) {
      console.error('Error adding to campaign:', err);
      let errorMsg = 'Failed to add influencer to campaign';
      
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail[0]?.msg || JSON.stringify(err.response.data.detail);
        } else {
          errorMsg = JSON.stringify(err.response.data.detail);
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const filtered = campaigns.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.objective.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-salty-card rounded-2xl w-full max-w-md border border-salty-border shadow-2xl flex flex-col max-h-[80vh]">
        <div className="p-5 border-b border-salty-border flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-salty-text-main flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-salty-purple" />
              Add to Campaign
            </h3>
            <p className="text-xs text-salty-text-muted mt-0.5">Influencer: {influencer?.name} (@{influencer?.instagram_handle})</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-salty-text-muted hover:text-salty-text-main">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 bg-salty-bg/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-salty-text-muted" />
            <Input 
              placeholder="Search campaigns..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-salty-bg border-salty-border text-sm"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-salty-text-muted">
              <Loader2 className="w-6 h-6 animate-spin text-salty-purple" />
              <p className="text-xs">Loading campaigns...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-20 text-salty-text-main" />
              <p className="text-sm text-salty-text-muted">{search ? 'No campaigns match your search' : 'No active or draft campaigns found'}</p>
            </div>
          ) : (
            filtered.map((campaign) => (
              <div 
                key={campaign.id}
                className="flex items-center justify-between p-4 rounded-xl border border-salty-border bg-salty-bg/50 hover:border-salty-purple/50 transition-all group"
              >
                <div className="min-w-0 flex-1 mr-4">
                  <h4 className="text-sm font-semibold text-salty-text-main group-hover:text-salty-purple-light transition-colors truncate">{campaign.name}</h4>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] font-bold uppercase py-0.5 px-1.5 bg-salty-purple/10 text-salty-purple rounded tracking-wider">
                      {campaign.objective}
                    </span>
                    <span className="text-[10px] font-medium text-salty-text-muted">
                      {campaign.influencer_count || 0} influencers
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAdd(campaign.id)}
                  disabled={saving === campaign.id}
                  className="border-salty-border hover:bg-salty-purple hover:border-salty-purple group-hover:bg-salty-purple group-hover:border-salty-purple text-xs font-bold"
                >
                  {saving === campaign.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Select'}
                </Button>
              </div>
            ))
          )}
        </div>

        <div className="p-4 border-t border-salty-border flex justify-end">
          <Button variant="ghost" onClick={onClose} className="text-salty-text-muted text-sm">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InfluencerDatabase;